// Custom fields in the ticket list (#9399).
//
// Inside a department view each field with "Show as a column" on gets its own
// column: its header sorts it (joining the numbered sort stack) and filters it
// the way the built-in headers do — option ticks for a pick-list or checkbox,
// a day range for a date, a from/to pair for a number. Everywhere else (All
// items mixes departments) one combined Fields column shows each row's values
// on one line as chips, truncated like the title, with every field on its own
// line in a hover card; its header sorts by any field and filters the
// pick-lists and checkboxes.
import { useState } from "react";
import { ListFilter, X } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "@/components/ui/hover-card";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import ColorBadge from "@/components/shared/ColorBadge";
import {
  HeaderDateFilter,
  HeaderFilter,
  HeaderOptionList,
  HeaderTrigger,
  SortSection,
  type HeaderSortControl,
} from "@/components/backlog/HeaderFilter";
import { cn } from "@/lib/utils";
import {
  fieldFilterOptions,
  formatFieldValue,
  isFieldFilterSet,
  optionColor,
  optionOf,
  type CustomField,
  type CustomFieldValues,
  type FieldFilter,
} from "@/lib/customFields";

interface FieldColumnHeaderProps {
  field: CustomField;
  sort: HeaderSortControl;
  filter: FieldFilter | undefined;
  onFilterChange: (filter: FieldFilter | undefined) => void;
}

/** One field's column header in a department view. */
export function FieldColumnHeader({ field, sort, filter, onFilterChange }: FieldColumnHeaderProps) {
  if (field.field_type === "select" || field.field_type === "checkbox") {
    return (
      <HeaderFilter
        label={field.name}
        options={fieldFilterOptions(field)}
        selected={filter?.values ?? []}
        onChange={(values) => onFilterChange({ values })}
        align="center"
        sort={sort}
        triggerClassName="justify-center"
      />
    );
  }
  if (field.field_type === "date") {
    return (
      <HeaderDateFilter
        label={field.name}
        sort={sort}
        range={{ from: filter?.from ?? null, to: filter?.to ?? null }}
        onRangeChange={(range) => onFilterChange(range)}
        triggerClassName="justify-center"
      />
    );
  }
  return <HeaderNumberOrTextFilter field={field} sort={sort} filter={filter} onFilterChange={onFilterChange} />;
}

/** Numbers sort and take a from/to range; text only sorts (the list search covers it). */
function HeaderNumberOrTextFilter({ field, sort, filter, onFilterChange }: FieldColumnHeaderProps) {
  const [open, setOpen] = useState(false);
  const isNumber = field.field_type === "number";
  const active = isNumber && isFieldFilterSet(filter);
  const set = (end: "from" | "to", raw: string) => {
    const text = raw.trim();
    onFilterChange({ ...filter, [end]: text === "" || !Number.isFinite(Number(text)) ? null : text });
  };
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <HeaderTrigger
          label={field.name}
          title={isNumber ? `Sort or filter by ${field.name}` : `Sort by ${field.name}`}
          filterCount={active ? 1 : 0}
          sortIndex={sort.index}
          sortDirection={sort.direction}
          restIcon={<ListFilter className="th-filter-icon h-3 w-3 shrink-0" />}
          className="justify-center"
        />
      </PopoverTrigger>
      <PopoverContent align="center" className="w-56 border-border bg-card p-0">
        <SortSection sort={sort} />
        {isNumber && (
          <div className="space-y-2 p-2">
            <p className="px-1 text-[10px] font-bold uppercase tracking-wide text-muted-foreground">Range</p>
            <div className="flex items-center gap-2">
              <Input
                aria-label={`${field.name} from`}
                inputMode="decimal"
                placeholder="From"
                defaultValue={filter?.from ?? ""}
                onChange={(e) => set("from", e.target.value)}
                className="h-8"
              />
              <Input
                aria-label={`${field.name} to`}
                inputMode="decimal"
                placeholder="To"
                defaultValue={filter?.to ?? ""}
                onChange={(e) => set("to", e.target.value)}
                className="h-8"
              />
            </div>
            {active && (
              <Button
                variant="ghost"
                size="sm"
                className="h-7 w-full justify-start gap-1.5 px-2 text-xs text-muted-foreground"
                onClick={() => {
                  onFilterChange(undefined);
                  setOpen(false);
                }}
              >
                <X className="h-3 w-3" />
                Clear {field.name.toLowerCase()} filter
              </Button>
            )}
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}

/** One field's value in its own column. */
export function FieldValueCell({ field, value }: { field: CustomField; value: unknown }) {
  const option = optionOf(field, value);
  const text = formatFieldValue(field, value);
  return (
    <td className="hidden w-28 px-2 py-2 text-center text-sm xl:table-cell">
      {option ? (
        <span className="inline-flex max-w-full justify-center">
          <ColorBadge label={option.label} color={optionColor(option)} shape="mini" />
        </span>
      ) : text !== null ? (
        <span className="block truncate text-muted-foreground" title={text}>
          {text}
        </span>
      ) : null}
    </td>
  );
}

/** A row's values in the combined Fields column: chips on one line, every field on its own line on hover. */
export function FieldsSummaryCell({
  fields,
  values,
}: {
  /** The fields that apply to this row and are shown in the list, in order. */
  fields: readonly CustomField[];
  values: CustomFieldValues;
}) {
  // Each value formatted once, for both the chips and the hover card.
  const rows = fields.map((f) => ({ field: f, text: formatFieldValue(f, values[f.id]) }));
  const filled = rows.filter((r) => r.text !== null);
  return (
    <td className="hidden w-48 px-2 py-2 text-sm xl:table-cell">
      {filled.length > 0 && (
        <HoverCard openDelay={250} closeDelay={80}>
          <HoverCardTrigger asChild>
            <div className="flex min-w-0 items-center gap-1 overflow-hidden whitespace-nowrap" tabIndex={0}>
              {filled.map(({ field, text }) => (
                <FieldChip key={field.id} field={field} value={values[field.id]} text={text!} />
              ))}
            </div>
          </HoverCardTrigger>
          <HoverCardContent align="start" className="w-64 border-border bg-card p-3">
            <dl className="space-y-1.5 text-sm">
              {rows.map(({ field, text }) => (
                <div key={field.id} className="flex items-baseline justify-between gap-3">
                  <dt className="shrink-0 text-xs font-semibold text-muted-foreground">{field.name}</dt>
                  <dd className="min-w-0 truncate text-right text-foreground">
                    {text ?? <span className="text-muted-foreground">—</span>}
                  </dd>
                </div>
              ))}
            </dl>
          </HoverCardContent>
        </HoverCard>
      )}
    </td>
  );
}

/** "Ranking 9" as plain text, or a pick-list option as its coloured chip. Only drawn for a filled value. */
function FieldChip({ field, value, text }: { field: CustomField; value: unknown; text: string }) {
  const option = optionOf(field, value);
  if (option) return <ColorBadge label={option.label} color={optionColor(option)} shape="mini" />;
  return (
    <span className="shrink-0 rounded border border-border px-1.5 text-[10px] leading-4 text-muted-foreground">
      <span className="font-semibold">{field.name}</span> {text}
    </span>
  );
}

interface FieldsColumnHeaderProps {
  fields: readonly CustomField[];
  sortFor: (field: CustomField) => HeaderSortControl;
  filters: Record<string, FieldFilter>;
  onFilterChange: (fieldId: string, filter: FieldFilter | undefined) => void;
}

/** The combined column's header: sort by any field, filter the pick-lists and checkboxes. */
export function FieldsColumnHeader({ fields, sortFor, filters, onFilterChange }: FieldsColumnHeaderProps) {
  const [open, setOpen] = useState(false);
  const [focus, setFocus] = useState<string | null>(null);
  const activeFilters = fields.filter((f) => isFieldFilterSet(filters[f.id])).length;
  const sorted = fields.map((f) => sortFor(f)).filter((s) => s.index !== null);
  const first = sorted.sort((a, b) => (a.index ?? 0) - (b.index ?? 0))[0];
  const current = fields.find((f) => f.id === focus) ?? fields[0];

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <HeaderTrigger
          label="Fields"
          title="Sort or filter by a custom field"
          filterCount={activeFilters}
          sortIndex={first?.index ?? null}
          sortDirection={first?.direction ?? null}
          restIcon={<ListFilter className="th-filter-icon h-3 w-3 shrink-0" />}
        />
      </PopoverTrigger>
      <PopoverContent align="end" className="w-64 border-border bg-card p-0">
        <div className="flex flex-wrap gap-1 border-b border-border p-2">
          {fields.map((f) => (
            <button
              key={f.id}
              type="button"
              onClick={() => setFocus(f.id)}
              className={cn(
                "rounded px-2 py-0.5 text-xs transition-colors",
                f.id === current?.id
                  ? "bg-[hsl(var(--ring)/0.15)] font-semibold text-brand"
                  : "text-foreground hover:bg-muted",
              )}
            >
              {f.name}
              {(isFieldFilterSet(filters[f.id]) || sortFor(f).index !== null) && (
                <span aria-hidden className="ml-1 text-brand">
                  •
                </span>
              )}
            </button>
          ))}
        </div>
        {current && (
          <>
            <SortSection sort={sortFor(current)} />
            {(current.field_type === "select" || current.field_type === "checkbox") && (
              <HeaderOptionList
                label={current.name}
                options={fieldFilterOptions(current)}
                selected={filters[current.id]?.values ?? []}
                onChange={(values) => onFilterChange(current.id, { values })}
              />
            )}
            {(current.field_type === "number" || current.field_type === "date") && (
              <p className="px-3 py-2 text-xs text-muted-foreground">
                Open a department to filter {current.name} by range.
              </p>
            )}
          </>
        )}
        {activeFilters > 0 && (
          <div className="border-t border-border p-1">
            <Button
              variant="ghost"
              size="sm"
              className="h-7 w-full justify-start gap-1.5 px-2 text-xs text-muted-foreground"
              onClick={() => fields.forEach((f) => onFilterChange(f.id, undefined))}
            >
              <X className="h-3 w-3" />
              Clear field filters
            </Button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
